// src/components/rules/RulesManagement.js
import React, { useState, useEffect } from 'react';
import { supabase, supabaseAdmin } from '../../config/supabase.config';
import '../../pages/UserDashboard.css';
import './CommunityRules.css';

function RulesManagement({ userData, onBack }) {
  const [rules, setRules] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
  const [formData, setFormData] = useState({
    category: 'general',
    rule_number: '',
    title: '',
    description: '',
    severity: 'normal',
    fine_amount: ''
  });

  const isAdmin = userData?.role === 'admin' || userData?.role === 'superadmin';

  useEffect(() => {
    fetchRules();
    if (darkMode) {
      document.body.classList.add('dark-mode');
    }
  }, [darkMode]);

  const fetchRules = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('community_rules')
        .select('*')
        .order('category')
        .order('rule_number');

      if (error) throw error;
      setRules(data || []);
    } catch (error) {
      console.error('Error fetching rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  
  if (!formData.title || !formData.description) {
    alert('Please fill in all required fields');
    return;
  }

  // Validation 1: Check for duplicate rule titles
  const duplicateTitle = rules.find(r => 
    r.title.toLowerCase() === formData.title.toLowerCase() && 
    r.id !== editingRule?.id
  );
  
  if (duplicateTitle) {
    alert('A rule with this title already exists');
    return;
  }

  // Validation 2: Check rule number format and uniqueness
  if (formData.rule_number) {
    const ruleNumberRegex = /^[A-Z]+\d+$/; // e.g., G1, P2, S1
    if (!ruleNumberRegex.test(formData.rule_number)) {
      alert('Rule number must be in format: Letter(s) followed by number (e.g., G1, P2)');
      return;
    }

    const duplicateNumber = rules.find(r => 
      r.rule_number === formData.rule_number && 
      r.id !== editingRule?.id
    );
    
    if (duplicateNumber) {
      alert('This rule number already exists');
      return;
    }
  }

  // Validation 3: Fine amount logic
  if (formData.severity === 'critical' && !formData.fine_amount) {
    if (!window.confirm('Critical rules usually have fines. Continue without fine?')) {
      return;
    }
  }

  setLoading(true);
  try {
    const client = isAdmin ? supabaseAdmin : supabase;
    
    // Auto-generate rule number if not provided
    let ruleNumber = formData.rule_number;
    if (!ruleNumber && !editingRule) {
      const categoryPrefix = {
        general: 'G',
        noise: 'N',
        parking: 'P',
        pets: 'PT',
        safety: 'S',
        common_areas: 'CA'
      };
      
      const prefix = categoryPrefix[formData.category] || 'R';
      const categoryRules = rules.filter(r => r.category === formData.category);
      const nextNumber = categoryRules.length + 1;
      ruleNumber = `${prefix}${nextNumber}`;
    }
    
    const ruleData = {
      ...formData,
      rule_number: ruleNumber,
      fine_amount: formData.fine_amount ? parseFloat(formData.fine_amount) : null,
      created_by: userData.id,
      is_active: true
    };

    if (editingRule) {
      const { error } = await client
        .from('community_rules')
        .update({
          ...ruleData,
          updated_at: new Date().toISOString(),
          updated_by: userData.id
        })
        .eq('id', editingRule.id);

      if (error) throw error;
      
      // Log the change
      await client
        .from('rule_audit_log')
        .insert({
          rule_id: editingRule.id,
          action: 'update',
          changed_by: userData.id,
          changes: JSON.stringify({
            before: editingRule,
            after: ruleData
          }),
          created_at: new Date().toISOString()
        });
      
      alert('Rule updated successfully!');
    } else {
      const { data, error } = await client
        .from('community_rules')
        .insert([ruleData])
        .select()
        .single();

      if (error) throw error;
      
      // Log the creation
      await client
        .from('rule_audit_log')
        .insert({
          rule_id: data.id,
          action: 'create',
          changed_by: userData.id,
          changes: JSON.stringify(ruleData),
          created_at: new Date().toISOString()
        });
      
      alert('Rule added successfully!');
    }

    resetForm();
    fetchRules();
  } catch (error) {
    console.error('Error saving rule:', error);
    if (error.code === '23505') {
      alert('This rule already exists');
    } else {
      alert('Failed to save rule');
    }
  } finally {
    setLoading(false);
  }
};
function RuleAcknowledgmentStatus({ ruleId }) {
  const [stats, setStats] = useState({ total: 0, acknowledged: 0 });
  
  useEffect(() => {
    fetchAcknowledgmentStats();
  }, [ruleId]);
  
  const fetchAcknowledgmentStats = async () => {
    const { data: residents } = await supabase
      .from('residents')
      .select('id')
      .eq('account_status', 'active');
    
    const { data: acknowledgments } = await supabase
      .from('rule_acknowledgments')
      .select('resident_id')
      .eq('rule_id', ruleId);
    
    setStats({
      total: residents?.length || 0,
      acknowledged: acknowledgments?.length || 0
    });
  };
  
  return (
    <span style={{ fontSize: '12px', color: '#666' }}>
      {stats.acknowledged}/{stats.total} acknowledged
    </span>
  );
}
  const handleEdit = (rule) => {
    setEditingRule(rule);
    setFormData({
      category: rule.category,
      rule_number: rule.rule_number || '',
      title: rule.title,
      description: rule.description,
      severity: rule.severity,
      fine_amount: rule.fine_amount || ''
    });
    setShowAddForm(true);
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`Delete rule: ${rule.title}?`)) return;

    try {
      const client = isAdmin ? supabaseAdmin : supabase;
      
      const { error } = await client
        .from('community_rules')
        .update({ is_active: false })
        .eq('id', rule.id);

      if (error) throw error;
      
      fetchRules();
      alert('Rule deleted successfully');
    } catch (error) {
      console.error('Error deleting rule:', error);
      alert('Failed to delete rule');
    }
  };

  const resetForm = () => {
    setFormData({
      category: 'general',
      rule_number: '',
      title: '',
      description: '',
      severity: 'normal',
      fine_amount: ''
    });
    setEditingRule(null);
    setShowAddForm(false);
  };

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    document.body.classList.toggle('dark-mode');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      normal: { bg: '#28a745', label: 'Normal' },
      important: { bg: '#ffc107', label: 'Important' },
      critical: { bg: '#dc3545', label: 'Critical' }
    };
    return colors[severity] || colors.normal;
  };

  if (!isAdmin) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
          <div className="dashboard-card" style={{ textAlign: 'center', padding: '40px' }}>
            <h3>Access Denied</h3>
            <p>Only administrators can manage rules.</p>
            <button onClick={onBack} className="btn-secondary">
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`dashboard-container ${darkMode ? 'dark-mode' : ''}`}>
      <div 
        className={`mobile-overlay ${mobileMenuOpen ? 'active' : ''}`}
        onClick={closeMobileMenu}
      ></div>

      {/* Sidebar Navigation */}
      <aside className={`dashboard-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="user-info">
            <div className="user-avatar">
              {userData?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="user-details">
              <h3>{userData?.name}</h3>
              <p>Flat {userData?.flat_number}</p>
            </div>
          </div>
        </div>
        
        <nav className="sidebar-menu">
          <button
            className="menu-item"
            onClick={onBack}
          >
            <span className="menu-icon">←</span>
            <span className="menu-label">Back to Rules</span>
          </button>
          
          <div className="sidebar-divider"></div>
          
          <button
            className="menu-item active"
            onClick={() => {
              setShowAddForm(false);
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">📋</span>
            <span className="menu-label">All Rules</span>
          </button>
          
          <button
            className="menu-item"
            onClick={() => {
              setShowAddForm(true);
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">➕</span>
            <span className="menu-label">Add New Rule</span>
          </button>
        </nav>
        
        <div className="sidebar-footer">
          <button className="theme-toggle" onClick={toggleDarkMode}>
            <span className="menu-icon">{darkMode ? '☀️' : '🌙'}</span>
            <span className="menu-label">{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
          </button>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className="dashboard-main">
        {/* Mobile Header */}
        <header className="dashboard-header enhanced">
          <div className="header-left">
            <button 
              className={`mobile-menu-btn ${mobileMenuOpen ? 'active' : ''}`}
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
            <h2 className="page-title-mobile">⚙️ Manage Rules</h2>
          </div>
        </header>

        <div className="dashboard-content">
          {/* Desktop Header */}
          <div className="rules-header">
            <h2 className="page-title">⚙️ Manage Community Rules</h2>
            <button
              onClick={() => setShowAddForm(true)}
              className="btn-primary"
            >
              + Add New Rule
            </button>
          </div>

          {/* Add/Edit Form */}
          {showAddForm && (
            <div className="dashboard-card form-card">
              <h3>{editingRule ? 'Edit Rule' : 'Add New Rule'}</h3>
              
              <form onSubmit={handleSubmit} className="rules-form">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Category *</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      className="form-select"
                      required
                    >
                      <option value="general">General</option>
                      <option value="noise">Noise</option>
                      <option value="parking">Parking</option>
                      <option value="pets">Pets</option>
                      <option value="safety">Safety</option>
                      <option value="common_areas">Common Areas</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Rule Number</label>
                    <input
                      type="text"
                      value={formData.rule_number}
                      onChange={(e) => setFormData({...formData, rule_number: e.target.value})}
                      className="form-input"
                      placeholder="e.g., G1, P2"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="form-input"
                    placeholder="Enter rule title"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Description *</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="form-textarea"
                    placeholder="Enter detailed rule description"
                    rows="4"
                    required
                  />
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label>Severity</label>
                    <select
                      value={formData.severity}
                      onChange={(e) => setFormData({...formData, severity: e.target.value})}
                      className="form-select"
                    >
                      <option value="normal">Normal</option>
                      <option value="important">Important</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Fine Amount (₹)</label>
                    <input
                      type="number"
                      value={formData.fine_amount}
                      onChange={(e) => setFormData({...formData, fine_amount: e.target.value})}
                      className="form-input"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary"
                  >
                    {loading ? 'Saving...' : (editingRule ? 'Update Rule' : 'Add Rule')}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Rules List */}
          <div className="dashboard-card">
            <h3>Existing Rules</h3>
            
            {loading ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading rules...</p>
              </div>
            ) : rules.length === 0 ? (
              <div className="empty-state">
                <p>No rules found</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Number</th>
                      <th>Title</th>
                      <th>Severity</th>
                      <th>Fine</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rules.map(rule => {
                      const severity = getSeverityBadge(rule.severity);
                      return (
                        <tr key={rule.id}>
                          <td>{rule.category}</td>
                          <td>{rule.rule_number || '-'}</td>
                          <td>{rule.title}</td>
                          <td>
                            <span 
                              className="status-badge"
                              style={{ 
                                background: severity.bg,
                                color: 'white',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px'
                              }}
                            >
                              {severity.label}
                            </span>
                          </td>
                          <td>{rule.fine_amount ? `₹${rule.fine_amount}` : '-'}</td>
                          <td>
                            <span style={{ color: rule.is_active ? '#28a745' : '#dc3545' }}>
                              {rule.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button
                                onClick={() => handleEdit(rule)}
                                className="btn-sm btn-warning"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(rule)}
                                className="btn-sm btn-danger"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default RulesManagement;